// noinspection DuplicatedCode,ES6PreferShortImport

import type { ContactPerson, Station } from '@app/icare/db/schema/common'
import { AcademicTitleEnum, AppEntity, SalutationEnum } from '@app/icare/db/schema/common'
import f from '@app/icare/db/schema/field/f'
import type { AdTeam } from '@app/icare/entities/adTeam/schema/ADTeam'
import type { CareService } from '@app/icare/entities/careService/schema/CareService'
import type { Clinic } from '@app/icare/entities/clinic/schema/Clinic'
import type { CooperationPartner } from '@app/icare/entities/cooperationPartner/schema/CooperationPartner'
import type { CostCarrier } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import type { Doctor } from '@app/icare/entities/doctor/schema/Doctor'
import type { Hospice } from '@app/icare/entities/hospice/schema/Hospice'
import type { MedicalSupplyHome } from '@app/icare/entities/medicalSupplyHome/schema/MedicalSupplyHome'
import type { MedicalTechnology } from '@app/icare/entities/medicalTechnology/schema/MedicalTechnology'
import type { NursingHome } from '@app/icare/entities/nursingHome/schema/NursingHome'
import type { Pharmacy } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import type { SyncableUser } from '@app/icare/entities/user/schema/User'
import type { ArrayElement } from '@app/icare/types/helpers'
import { msg } from '@lingui/core/macro'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { z } from 'zod'
import type { PatientADTeamRelation } from './PatientADTeam'
import { PatientADTeamRelationSchema } from './PatientADTeam'
import type { PatientAddressRelation } from './PatientAddress'
import { PatientAddressRelationSchema } from './PatientAddress'
import type { PatientApplicantRelation } from './PatientApplicant'
import { PatientApplicantRelationSchema } from './PatientApplicant'
import type { PatientCareLevelRelation } from './PatientCareLevel'
import { PatientCareLevelRelationSchema } from './PatientCareLevel'
import type { PatientCareManagerRelation } from './PatientCareManager'
import { PatientCareManagerRelationSchema } from './PatientCareManager'
import {
  NotNeededReasonEnum,
  type PatientCareServiceRelation,
  PatientCareServiceRelationSchema,
} from './PatientCareService'
import type { PatientClinicRelation } from './PatientClinic'
import { PatientClinicRelationSchema } from './PatientClinic'
import type { PatientCooperationPartnerRelation } from './PatientCooperationPartner'
import { PatientCooperationPartnerRelationSchema } from './PatientCooperationPartner'
import type { PatientCostCarrierRelation } from './PatientCostCarrier'
import { PatientCostCarrierRelationSchema } from './PatientCostCarrier'
import type { PatientCostCarrierExemption } from './PatientCostCarrierExemption'
import { PatientCostCarrierExemptionSchema } from './PatientCostCarrierExemption'
import type { PatientDoctorRelation } from './PatientDoctor'
import { PatientDoctorRelationSchema } from './PatientDoctor'
import { PatientId } from './PatientId'

export type { PatientIdType } from './PatientId'
export { PatientId } from './PatientId'

import type { PatientMedicalSupplyAccessRelation } from './PatientMedicalSupplyAccess'
import { PatientMedicalSupplyAccessRelationSchema } from './PatientMedicalSupplyAccess'
import type { PatientMedicalTechnologyRelation } from './PatientMedicalTechnology'
import { PatientMedicalTechnologyRelationSchema } from './PatientMedicalTechnology'
import type { PatientMobilityRelation } from './PatientMobility'
import { PatientMobilityRelationSchema } from './PatientMobility'
import type { PatientPharmacyRelation } from './PatientPharmacy'
import { PatientPharmacyRelationSchema } from './PatientPharmacy'
import type { PatientRelativeRelation } from './PatientRelative'
import { PatientRelativeRelationSchema } from './PatientRelative'
import type { PatientSeparateResidenceRelation } from './PatientSeparateResidence'
import { PatientSeparateResidenceRelationSchema } from './PatientSeparateResidence'
import type { PatientTherapyRelation } from './PatientTherapy'
import { PatientTherapyRelationSchema } from './PatientTherapy'
import type { PatientWeightRelation } from './PatientWeight'
import { PatientWeightRelationSchema } from './PatientWeight'

export const PatientStatusEnum = defineEnum({
  new: msg`Neu`,
  open: msg`Offen`,
  closed: msg`Geschlossen`,
})

export type PatientStatusType = typeof PatientStatusEnum.$.Type

export const PatientStatusNewSchema = z.object({
  value: z.literal(PatientStatusEnum.new),
  comment: z.string().nullish(),
})

export const PatientStatusOpenSchema = z.object({
  value: z.literal(PatientStatusEnum.open),
  comment: z.string().nullish(),
})

export const PatientStatusClosedSchema = z.object({
  value: z.literal(PatientStatusEnum.closed),
  comment: z.string().nullish(),
})

export const PatientStatusSchema = z.discriminatedUnion('value', [
  PatientStatusNewSchema,
  PatientStatusOpenSchema,
  PatientStatusClosedSchema,
])

export type PatientStatus = z.infer<typeof PatientStatusSchema>

export type PatientPrescriberRelation = PatientClinicRelation | PatientDoctorRelation

export const DiagnosisTypeEnum = defineEnum({
  select: msg`Auswahl`,
  custom: msg`Benutzerdefiniert`,
})

export type DiagnosisTypeType = typeof DiagnosisTypeEnum.$.Type

export const DiagnosisSelectSchema = z.object({
  type: z.enum(DiagnosisTypeEnum.$.zodEnum()),
  value: z.coerce.string(),
})

export type DiagnosisSelect = z.infer<typeof DiagnosisSelectSchema>

export const PatientSchema = AppEntity.extend({
  id: PatientId.schema,
  status: PatientStatusSchema,
  personal: z
    .object({
      salutation: z.enum(SalutationEnum.$.zodEnum()).nullish(),
      honorificPrefix: z.enum(AcademicTitleEnum.$.zodEnum()).nullish(),
      firstName: z.string().nullish(),
      lastName: z.string().min(1).nullish(),
      birthdate: z.iso.date().nullish(),
      height: f.number().nullish(),
    })
    .nullish(),
  contact: z
    .object({
      email: z.string().nullish(),
      phone: z.string().nullish(),
      mobile: z.string().nullish(),
      fax: z.string().nullish(),
    })
    .nullish(),
  address: z
    .object({
      streetAddress: z.string().nullish(),
      zipCode: z.string().nullish(),
      city: z.string().nullish(),
    })
    .nullish(),
  anamnesis: z
    .object({
      diagnosis: z
        .array(DiagnosisSelectSchema)
        .nullish()
        .default(() => []),
      additionalInfo: z.string().nullish(),
    })
    .nullish(),
  comment: z.string().nullish(),
  deceased: z
    .object({
      deceased: z.boolean().nullish(),
      recordedDate: f.date().nullish(),
      date: f.date().nullish(),
      approximatelyDate: z
        .object({
          startDate: f.date().nullish(),
          endDate: f.date().nullish(),
        })
        .nullish(),
      comment: z.string().nullish(),
    })
    .nullish(),
  careService: z
    .object({
      notNeeded: z.boolean().nullish().default(false),
      notNeededReason: z.enum(NotNeededReasonEnum.$.zodEnum()).nullish(),
      notNeededAdditionalReason: z.string().nullish(),
    })
    .nullish(),
  costCarrier: z
    .object({
      privateInsured: z.boolean().nullish().default(false),
    })
    .nullish(),
  clinic: z
    .object({
      noPrescriber: z.boolean().default(false),
      notNeeded: z.boolean().default(false),
    })
    .nullish(),
  doctor: z
    .object({
      noPrescriber: z.boolean().default(false),
    })
    .nullish(),
})

export const PatientWithRelationSchema = PatientSchema.extend({
  adTeams: z.array(PatientADTeamRelationSchema).nullish(),
  mobilities: z.array(PatientMobilityRelationSchema).nullish(),
  weights: z.array(PatientWeightRelationSchema).nullish(),
  careLevels: z.array(PatientCareLevelRelationSchema).nullish(),
  careServices: z.array(PatientCareServiceRelationSchema).nullish(),
  relatives: z.array(PatientRelativeRelationSchema).nullish(),
  therapies: z.array(PatientTherapyRelationSchema).nullish(),
  applicants: z.array(PatientApplicantRelationSchema).nullish(),
  pharmacies: z.array(PatientPharmacyRelationSchema).nullish(),
  medicalTechnologies: z.array(PatientMedicalTechnologyRelationSchema).nullish(),
  cooperationPartners: z.array(PatientCooperationPartnerRelationSchema).nullish(),
  costCarriers: z.array(PatientCostCarrierRelationSchema).nullish(),
  costCarrierExemptions: z.array(PatientCostCarrierExemptionSchema).nullish(),
  separateResidences: z.array(PatientSeparateResidenceRelationSchema).nullish(),
  clinics: z.array(PatientClinicRelationSchema).nullish(),
  doctors: z.array(PatientDoctorRelationSchema).nullish(),
  medicalSupplyAccess: z.array(PatientMedicalSupplyAccessRelationSchema).nullish(),
  careManagers: z.array(PatientCareManagerRelationSchema).nullish(),
  // sage: SageSyncSchema.nullish(),
  addresses: z.array(PatientAddressRelationSchema).nullish(),
})

export type Patient = z.infer<typeof PatientSchema>

export type PatientWithRelations = Patient & PatientRelations

export interface PatientRelations {
  adTeams: WithRestoredPatientRelations<PatientADTeamRelation, AdTeam>[]
  addresses: PatientAddressRelation[]
  mobilities: PatientMobilityRelation[]
  weights: PatientWeightRelation[]
  careLevels: PatientCareLevelRelation[]
  careServices: WithRestoredPatientRelations<PatientCareServiceRelation, CareService>[]
  relatives: PatientRelativeRelation[]
  therapies: PatientTherapyRelation[]
  applicants: WithRestoredPatientRelations<
    PatientApplicantRelation,
    Clinic | Hospice | Pharmacy | CostCarrier | CareService | Doctor | MedicalSupplyHome | NursingHome
  >[]
  pharmacies: WithRestoredPatientRelations<PatientPharmacyRelation, Pharmacy>[]
  medicalTechnologies: WithRestoredPatientRelations<PatientMedicalTechnologyRelation, MedicalTechnology>[]
  cooperationPartners: WithRestoredPatientRelations<PatientCooperationPartnerRelation, CooperationPartner>[]
  costCarriers: WithRestoredPatientRelations<PatientCostCarrierRelation, CostCarrier>[]
  costCarrierExemptions: PatientCostCarrierExemption[]
  separateResidences: WithRestoredPatientRelations<PatientSeparateResidenceRelation, Clinic | Hospice | NursingHome>[]
  clinics: WithRestoredPatientRelations<PatientClinicRelation, Clinic>[]
  doctors: WithRestoredPatientRelations<PatientDoctorRelation, Doctor>[]
  medicalSupplyAccess: PatientMedicalSupplyAccessRelation[]
  careManagers: WithRestoredPatientRelations<PatientCareManagerRelation, SyncableUser>[]
}

export type PatientRelationName = keyof PatientRelations

export const PatientEntityRelationEnum = defineEnum({
  adTeams: msg`AD-Teams`,
  pharmacies: msg`Apotheken`,
  careServices: msg`Pflegedienste`,
  medicalTechnologies: msg`Medizintechnik`,
  cooperationPartners: msg`Kooperationspartner`,
  costCarriers: msg`Kostenträger`,
  clinics: msg`Kliniken`,
  doctors: msg`Ärzte`,
  careManagers: msg`Mitarbeiter`,
})

export type PatientEntityRelationType = typeof PatientEntityRelationEnum.$.Type

export const patientEntityRelationTables = {
  adTeams: 'patientAdTeam',
  pharmacies: 'patientPharmacy',
  careServices: 'patientCareService',
  medicalTechnologies: 'patientMedicalTechnology',
  cooperationPartners: 'patientCooperationPartner',
  costCarriers: 'patientCostCarrier',
  clinics: 'patientClinic',
  doctors: 'patientDoctor',
  careManagers: 'patientCareManager',
} as const satisfies Record<PatientEntityRelationType, string>

export type PatientEntityRelationTables = (typeof patientEntityRelationTables)[keyof typeof patientEntityRelationTables]

export type WithRestoredPatientRelations<R, T> = Omit<R, 'contactPersons' | 'stations'> & {
  to: T
  contactPersons: R extends { contactPersons: unknown } ? (ContactPerson & ArrayElement<R['contactPersons']>)[] : never
  stations: R extends { stations: unknown } ? (Station & ArrayElement<R['stations']>)[] : never
}

export type PatientRelationType<R extends keyof PatientRelations> = PatientRelations[R]

export type PatientWithRelation<R extends keyof PatientRelations> = Patient & Pick<PatientRelations, R>

export type PatientReference = Partial<Patient> & { id: typeof PatientId.ref }

/**
 * PatientStatus description translations (additional metadata)
 */
export const patientStatusDescriptionMessages = {
  [PatientStatusEnum.new]: msg`Patient hat diesen Status, wenn er frisch angelegt wurde.`,
  [PatientStatusEnum.open]: msg`Der Status wird manuell auf offen gesetzt, wenn alle wichtigen Stammdaten vorliegen und mit dem Patienten gearbeitet werden kann.`,
  [PatientStatusEnum.closed]: msg`Der Patient hat diesen Status, wenn er verstorben ist, die Versorgung aus anderen Gründen beendet wurde und die Akte archiviert werden soll.`,
} as const
